import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BrandingContext = createContext(null);

const DEFAULTS = {
  platform_name: 'RestroPulse',
  tagline: 'Smarter Operations. Stronger Restaurants.',
  login_welcome_text: 'Welcome back — sign in to continue.',
  login_use_outlet_logo: false,
  login_outlet_id: null,
  has_logo: false,
  has_favicon: false,
};

/** Platform-branding provider. Fetches once on mount and exposes
 *  `platformName`, `logoUrl`, `faviconUrl`, plus a `refresh()` for
 *  live updates after Super Admin saves. */
export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULTS);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/branding/platform`);
      setBranding(b => ({ ...DEFAULTS, ...data, _v: Date.now() }));
    } catch (e) {
      setBranding(b => ({ ...b, _v: Date.now() }));
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Push document.title + favicon in response to branding changes.
  useEffect(() => {
    if (!ready) return;
    document.title = `${branding.platform_name} | ${branding.tagline || 'Operations'}`;
    if (branding.has_favicon) {
      const href = `${API}/branding/platform/favicon?v=${branding._v || ''}`;
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = href;
    }
  }, [branding, ready]);

  // Derived URLs — the _v cache-buster makes live updates instant.
  const logoUrl = branding.has_logo
    ? `${API}/branding/platform/logo?v=${branding._v || ''}`
    : '/restropulse-logo.png';

  const value = {
    branding,
    ready,
    refresh,
    platformName: branding.platform_name,
    tagline: branding.tagline,
    loginWelcomeText: branding.login_welcome_text,
    logoUrl,
    faviconUrl: branding.has_favicon ? `${API}/branding/platform/favicon?v=${branding._v || ''}` : '/favicon.ico',
    loginUseOutletLogo: !!branding.login_use_outlet_logo,
    loginOutletId: branding.login_outlet_id || null,
  };

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used inside <BrandingProvider>');
  return ctx;
}
